import React from "react";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MapPinOff,
  Navigation,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { GEOFENCE_STATUS } from "../../services/geofenceService";

export default function GeoFenceStatus({
  status,
  distance,
  accuracy,
  radius = 100,
  siteName,
  onRetry,
  compact = false,
}) {
  if (!status || status === GEOFENCE_STATUS.IDLE) {
    return null;
  }

  // Visual configuration mapping
  const getStatusConfig = () => {
    switch (status) {
      case GEOFENCE_STATUS.WITHIN_SITE:
        return {
          icon: CheckCircle2,
          iconColor: "text-emerald-500",
          bgColor: "bg-emerald-50 border-emerald-200",
          textColor: "text-emerald-900",
          title: "Within Site",
          badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-300",
          desc: `You are ${Math.round(distance || 0)}m from the site center.`,
          accuracyText: accuracy ? `GPS Accuracy: ±${Math.round(accuracy)}m` : null,
        };

      case GEOFENCE_STATUS.OUTSIDE_SITE:
        return {
          icon: XCircle,
          iconColor: "text-red-500",
          bgColor: "bg-red-50 border-red-200",
          textColor: "text-red-900",
          title: "Outside Site",
          badgeColor: "bg-red-100 text-red-800 border-red-300",
          desc: `You are ${Math.round(distance || 0)}m from the site center. You must be within ${radius}m to perform this action.`,
          accuracyText: accuracy ? `GPS Accuracy: ±${Math.round(accuracy)}m` : null,
        };

      case GEOFENCE_STATUS.LOCATION_ACCURACY_TOO_LOW:
        return {
          icon: AlertTriangle,
          iconColor: "text-amber-500",
          bgColor: "bg-amber-50 border-amber-200",
          textColor: "text-amber-900",
          title: "Location Accuracy Too Low",
          badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
          desc: "Unable to reliably verify your location. Please enable high-accuracy GPS and try again.",
          accuracyText: accuracy ? `Current Accuracy: ±${Math.round(accuracy)}m (Required: ±25m or better)` : null,
        };

      case GEOFENCE_STATUS.LOCATION_PERMISSION_DENIED:
        return {
          icon: ShieldAlert,
          iconColor: "text-red-500",
          bgColor: "bg-red-50 border-red-200",
          textColor: "text-red-900",
          title: "Location Access Required",
          badgeColor: "bg-red-100 text-red-800 border-red-300",
          desc: "Please allow browser location permission to perform this action.",
          accuracyText: null,
        };

      case GEOFENCE_STATUS.LOCATION_UNAVAILABLE:
      case GEOFENCE_STATUS.LOCATION_TIMEOUT:
        return {
          icon: MapPinOff,
          iconColor: "text-amber-500",
          bgColor: "bg-amber-50 border-amber-200",
          textColor: "text-amber-900",
          title: "GPS Unavailable",
          badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
          desc: "Unable to determine your current location. Please check your device location settings and try again.",
          accuracyText: null,
        };

      case GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED:
        return {
          icon: MapPinOff,
          iconColor: "text-amber-500",
          bgColor: "bg-amber-50 border-amber-200",
          textColor: "text-amber-900",
          title: "Site Location Not Configured",
          badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
          desc: siteName
            ? `The geo-fence for "${siteName}" has not been configured. Please contact an administrator.`
            : "This site's geo-fence has not been configured. Please contact an administrator.",
          accuracyText: null,
        };

      case GEOFENCE_STATUS.SITE_GEOFENCE_DISABLED:
        return {
          icon: CheckCircle2,
          iconColor: "text-blue-500",
          bgColor: "bg-blue-50 border-blue-200",
          textColor: "text-blue-900",
          title: "Geo-Fence Disabled",
          badgeColor: "bg-blue-100 text-blue-800 border-blue-300",
          desc: "Site geo-fencing is currently disabled for this location.",
          accuracyText: null,
        };

      case GEOFENCE_STATUS.LOADING:
        return {
          icon: Navigation,
          iconColor: "text-indigo-500 animate-spin",
          bgColor: "bg-indigo-50 border-indigo-200",
          textColor: "text-indigo-900",
          title: "Checking Site Location...",
          badgeColor: "bg-indigo-100 text-indigo-800 border-indigo-300",
          desc: "Capturing high-accuracy GPS coordinates...",
          accuracyText: null,
        };

      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const IconComponent = config.icon;

  if (compact) {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${config.badgeColor}`}
      >
        <IconComponent className="w-4 h-4 shrink-0" />
        <span>{config.title}</span>
        {distance !== null && distance !== undefined && (
          <span className="opacity-80">({Math.round(distance)}m)</span>
        )}
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl border transition-all ${config.bgColor} ${config.textColor}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <IconComponent className={`w-6 h-6 mt-0.5 shrink-0 ${config.iconColor}`} />
          <div>
            <h4 className="font-semibold text-sm sm:text-base leading-snug flex items-center gap-2">
              {config.title}
              {siteName && (
                <span className="text-xs px-2 py-0.5 rounded-md bg-white/70 border font-normal">
                  {siteName}
                </span>
              )}
            </h4>
            <p className="text-xs sm:text-sm mt-1 opacity-90">{config.desc}</p>
            {config.accuracyText && (
              <p className="text-xs mt-1.5 font-medium opacity-75">
                {config.accuracyText}
              </p>
            )}
          </div>
        </div>

        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-1.5 rounded-lg border text-xs font-semibold bg-white shadow-sm hover:bg-gray-50 flex items-center gap-1.5 shrink-0 transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Retry</span>
          </button>
        )}
      </div>
    </div>
  );
}
